import { Box, Collapse, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { ExtractedColor } from "../../types";

const formatLab = (value: number, prefix: string) => {
  const sign = value >= 0 ? "+" : "";
  return `${prefix}${sign}${Math.round(value)}`;
};

const ColorDetailCard = ({ color }: { color: ExtractedColor }) => {
  const [r, g, b] = color.rgb;
  const [l, a, bVal] = color.cieLab;
  const lum = r * 0.299 + g * 0.587 + b * 0.114;
  const textOnColor = lum > 140 ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.75)";

  const rows = [
    { label: "HEX", value: `#${color.hex.toUpperCase()}` },
    { label: "sRGB", value: `${r}, ${g}, ${b}` },
    {
      label: "CIELab",
      value: `${formatLab(l, "L")} ${formatLab(a, "a")} ${formatLab(bVal, "b")}`,
    },
  ];

  return (
    <Box
      sx={{
        marginTop: "var(--spacing8, 8px)",
        borderRadius: "var(--radius, 6px)",
        border: "1px solid var(--alias-stroke-on-white-default, #d9d9d9)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          height: 52,
          backgroundColor: `rgb(${r}, ${g}, ${b})`,
          display: "flex",
          alignItems: "flex-end",
          padding: "0 12px 8px",
        }}
      >
        <Typography
          sx={{
            fontFamily: 'var(--font-family, "AktivGrotesk")',
            fontWeight: 500,
            fontSize: "var(--body-b3-12-regular-size, 12px)",
            color: textOnColor,
          }}
        >
          #{color.hex.toUpperCase()}
        </Typography>
      </Box>
      <Box
        sx={{
          backgroundColor: "var(--alias-surface-low, #f5f5f5)",
          padding: "10px 12px",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}
      >
        {rows.map((row, i) => (
          <Box key={row.label}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "2px 0",
              }}
            >
              <Typography
                sx={{
                  fontSize: "var(--footnote-f1-10-regular-size, 10px)",
                  color: "var(--alias-text-on-white-secondary, #666)",
                  letterSpacing: "0.04em",
                }}
              >
                {row.label}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'var(--font-family, "AktivGrotesk")',
                  fontWeight: 500,
                  fontSize: "var(--body-b3-12-regular-size, 12px)",
                  color: "var(--alias-text-on-white-primary, #1a1a1a)",
                  letterSpacing: "0.03em",
                }}
              >
                {row.value}
              </Typography>
            </Box>
            {i < rows.length - 1 && (
              <Box
                sx={{
                  height: 1,
                  backgroundColor:
                    "var(--alias-stroke-on-white-default, #d9d9d9)",
                  marginTop: "6px",
                }}
              />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

interface ColorPalettePanelProps {
  palette: ExtractedColor[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}

export const ColorPalettePanel = ({
  palette,
  selectedIndex,
  onSelect,
}: ColorPalettePanelProps) => {
  const { t } = useTranslation();
  const selectedColor = selectedIndex >= 0 ? palette[selectedIndex] : null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "var(--spacing8, 8px)",
        }}
      >
        <Typography
          sx={{
            fontSize: "var(--body-b3-12-regular-size, 12px)",
            color: "var(--alias-text-on-white-primary, #1a1a1a)",
            fontFamily: 'var(--font-family, "AktivGrotesk")',
            fontWeight: 500,
          }}
        >
          {t(
            "ldp_explore_image_results_extracted_palette",
            "Extracted Color Palette",
          )}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: "var(--spacing8, 8px)",
          flexWrap: "wrap",
        }}
      >
        {palette.map((color, index) => {
          const isSelected = index === selectedIndex;
          return (
            <Box
              key={color.hex}
              onClick={() => onSelect(index)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "var(--spacing4, 4px)",
                cursor: "pointer",
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "4px",
                  backgroundColor: `rgb(${color.rgb[0]}, ${color.rgb[1]}, ${color.rgb[2]})`,
                  border: isSelected
                    ? "2px solid var(--alias-text-on-white-brand, #009FE4)"
                    : "2px solid var(--alias-stroke-on-white-default, #d9d9d9)",
                  transition: "border 0.15s ease",
                }}
              />
              <Typography
                sx={{
                  fontSize: "9px",
                  color: isSelected
                    ? "var(--alias-text-on-white-brand, #009FE4)"
                    : "var(--alias-text-on-white-disable, #999)",
                  fontFamily: "monospace",
                  lineHeight: 1,
                  textAlign: "center",
                  letterSpacing: "0.18px",
                }}
              >
                {color.hex}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Collapse in={!!selectedColor} unmountOnExit>
        {selectedColor && <ColorDetailCard color={selectedColor} />}
      </Collapse>
    </Box>
  );
};
